import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { StockRecord, AdministrationStatus } from '../../types'
import {
  getActionTitle,
  getActionSubtitle,
  getActionIcon
} from '../../utils/stockRecordUtils'
import {
  getAdministrationStatusDisplay,
  getAdministrationSubtitle
} from '../../utils/administrationUtils'
import Comment from '../../iconsV2/Comment'

interface ActionCellProps {
  record: StockRecord
  t: (key: string) => string
}

const ActionCell = ({ record, t }: ActionCellProps) => {
  if (record.type === 'administration' && record.administrationStatus) {
    const statusDisplay = getAdministrationStatusDisplay(
      record.administrationStatus
    )
    const isMissed = record.administrationStatus === AdministrationStatus.MISSED
    const isPaused = record.administrationStatus === AdministrationStatus.PAUSED
    const doseQuantity =
      (Math.abs(record.quantityChange ?? 0) ||
        record.administration?.doseQuantity) ??
      0

    const subtitle = getAdministrationSubtitle({
      status: record.administrationStatus,
      doseQuantity,
      unit:
        record.unit ||
        record.quantityType ||
        record?.administration?.quantityType ||
        '',
      quantityChange: record.quantityChange,
      t
    })

    return (
      <ActionCellContainer>
        <AdministrationIconContainer $isMissed={isMissed} $isPaused={isPaused}>
          {statusDisplay.display}
        </AdministrationIconContainer>
        <TextContainer>
          <Title>{t(statusDisplay.translationKey)}</Title>
          {subtitle && <Subtitle level={2}>{subtitle}</Subtitle>}
        </TextContainer>
      </ActionCellContainer>
    )
  }

  if (record.type === 'comment') {
    const notes = Array.isArray(record.notes)
      ? record.notes.join(', ')
      : record.notes || ''

    return (
      <ActionCellContainer>
        <IconContainer>
          <Comment />
        </IconContainer>
        <TextContainer>
          <Title>{t('COMMENT')}</Title>
          {notes && <Subtitle level={2}>{notes}</Subtitle>}
        </TextContainer>
      </ActionCellContainer>
    )
  }

  if (!record.action) {
    return (
      <ActionCellContainer>
        <TextContainer>
          <Title>-</Title>
        </TextContainer>
      </ActionCellContainer>
    )
  }

  const title = getActionTitle(record.action, t, record.administrationStatus)
  const subtitle = getActionSubtitle(
    record.action,
    record.quantityChange || 0,
    record.unit || record?.administration?.quantityType || '',
    record.notes
  )

  return (
    <ActionCellContainer>
      <IconContainer>{getActionIcon(record.action)}</IconContainer>
      <TextContainer>
        <Title>{title}</Title>
        {subtitle && <Subtitle level={2}>{subtitle}</Subtitle>}
      </TextContainer>
    </ActionCellContainer>
  )
}

const ActionCellContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const AdministrationIconContainer = styled.div<{
  $isMissed: boolean
  $isPaused: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $isMissed, $isPaused, theme }) =>
    $isMissed
      ? theme.color.status.danger
      : $isPaused
        ? theme.color.neutral.color5
        : theme.color.neutral.color5};
  width: 16px;
  height: 16px;
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const Title = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  line-height: 1;
`

const Subtitle = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

export default ActionCell
